import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import UserIcon from '~/components/icons/users/48px_circle-08.svg'
import { useKeycloak } from '~/components/keycloak'
import { colors } from '~/design'
import Logo from '~/meta/sebraeLogo.svg'
import { AppError } from '~/prix'
import Handle from '~/prix/react/components/handle'
import Link from '~/prix/react/components/link'
import keycloakConfig from '~/utils/shared/keycloakConfig'
import { resetAnalyticsUser } from '~/utils/client/analytics'

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: white !important;

  @media screen and (max-width: 340px) {
    min-width: auto;
    width: 100%;
  }

  form {
    margin-top: -15px;
  }
  .label {
    flex: 1;
    text-align: left;
    font-size: 14px;
  }
  .label :last-child {
    display: none;
  }
  .input[id='email'] {
    margin-bottom: 5px;
  }
  .input[id='password'] {
    margin-bottom: 5px;
  }
  input[type='submit'] {
    width: 100%;
    height: 35px;
    font-weight: bold;
    &:hover {
      text-decoration: none;
      background-color: ${colors.oceanBlue};
      cursor: pointer;
      transition-duration: 0.3s;
    }
  }
  #device {
    display: none;
  }
`

const Box = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 5px;
  padding: 30px;
  min-width: 275px;
  max-width: 450px;

  @media screen and (max-width: 340px) {
    min-width: auto;
  }

  .logo {
    flex-direction: row;
    color: white;
  }

  small {
    margin-top: 10px;
    font-size: 14px;
    /* color: #00000089; */
  }

  svg {
    fill: #fff;
  }
`

const Container = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const IconContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  align-self: center;
`

const EmailText = styled.span`
  padding: 15px 0px 15px 0px;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150ch;
  text-align: center;
  margin-top: 5px;
  font-size: 16px;
  color: white;
`

const Button = styled(Link)`
  display: block;
  width: 100%;
  text-decoration: none;
  background-color: white;
  color: ${colors.sebraeBlue};
  font-weight: bold;
  padding: 10px;
  transform: translateZ(0) scale(1);
  transform-origin: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  will-change: transform;
  text-align: center;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    transform: translateZ(0) scale(1.02);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  }
`

const SubLink = styled(Link)`
  display: block;
  padding: 5px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.lightBlue};

  &:hover {
    color: white;
  }
`

const LinkTerms = styled(Link)`
  font-weight: 600;
  color: ${colors.lightBlue};
  &:hover {
    color: white;
  }
`

export default function LoginComponent() {
  const navigate = useNavigate()
  const {
    isLoading,
    isAuthenticated,
    login,
    logout,
    tokenParsed,
    bypassAuth,
    error: generalAuthError,
  } = useKeycloak()
  const [loginOrLogoutError, setLoginOrLogoutError] = useState<Error | AppError | null>(null)

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (isAuthenticated && tokenParsed) {
      const redirectOnAuthSuccess = sessionStorage.getItem('redirectOnAuthSuccess')
      if (redirectOnAuthSuccess) {
        sessionStorage.removeItem('redirectOnAuthSuccess')
        navigate(redirectOnAuthSuccess)
      }
    }
  }, [isLoading, isAuthenticated, tokenParsed, navigate])

  const startLogin = useCallback(
    async () => {
      setLoginOrLogoutError(null)
      try {
        sessionStorage.setItem('redirectOnAuthSuccess', '/app')
        await login()
      } catch (error) {
        setLoginOrLogoutError(error as Error)
      }
    },
    [login],
  )

  const handleLogin = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      await startLogin()
    },
    [startLogin],
  )

  const handleLogout = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault()
      setLoginOrLogoutError(null)
      try {
        resetAnalyticsUser()
        await logout()
      } catch (error) {
        setLoginOrLogoutError(error as Error)
      }
    },
    [logout],
  )

  const handleReload = useCallback(async () => {
    window.location.reload()
  }, [])

  const error = generalAuthError ?? loginOrLogoutError
  const shouldShowLocalNetworkAccessHelp =
    error instanceof AppError && error.payload?.shouldShowLocalNetworkAccessHelp === true

  if (isAuthenticated && tokenParsed) {
    return (
      <Wrapper>
        <Box>
          <Handle
            error={error}
            errorTextColor='#fff'
            accessLinkColor={colors.lightBlue}
            accessLinkHoverColor='#fff'
            isLoading={isLoading}
            onTryAgain={shouldShowLocalNetworkAccessHelp ? startLogin : handleReload}
          >
            <Container>
              <IconContainer>
                <UserIcon height={65} width={65} fill='#fff' />
              </IconContainer>
              <EmailText> {tokenParsed.email} </EmailText>

              <Button href='/app'>Continuar como {tokenParsed.name}</Button>
              <br />
              <SubLink remote newTab href='/terms'>
                Termos de uso
              </SubLink>
              {!bypassAuth ? (
                <SubLink
                  remote
                  newTab
                  href={`${keycloakConfig.url}/realms/${keycloakConfig.realm}/account`}
                >
                  Conta
                </SubLink>
              ) : (
                <span>⚠️ Autenticado para desenvolvimento</span>
              )}
              <SubLink onClick={handleLogout}>Sair</SubLink>
            </Container>
          </Handle>
        </Box>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Box>
        <Handle
          error={error}
          errorTextColor='#fff'
          accessLinkColor={colors.lightBlue}
          accessLinkHoverColor='#fff'
          isLoading={isLoading}
          onTryAgain={shouldShowLocalNetworkAccessHelp ? startLogin : handleReload}
        >
          <div className='logo'>
            <Logo height={170} width={170} />
          </div>
          <Button onClick={handleLogin}>ACESSAR</Button>
          {bypassAuth ? <span>⚠️ Autenticação para desenvolvimento</span> : null}
          <small>
            Prosseguindo você concorda com os{' '}
            <LinkTerms newTab href='/terms'>
              {' '}
              termos de uso
            </LinkTerms>
            .
          </small>
          <small>
            Precisa de ajuda?{' '}
            <LinkTerms href='mailto:afonso.silva@sebrae.com.br'>Contato</LinkTerms> <span>•</span>{' '}
            <LinkTerms newTab href='https://forms.office.com/r/K0K66VYKBV'>
              Solicitar acesso
            </LinkTerms>
          </small>
        </Handle>
      </Box>
    </Wrapper>
  )
}
